import React, {
  forwardRef, memo, useCallback, useMemo,
} from 'react';
import { func } from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { getIndicator, panelsVisibilityShape } from './helpers';
import { isUploadStarted, requiredFields as requiredCustomFieldsSelector } from '../../store/import/selectors';
import { addToListField, removeFromListField } from '../../store/import/actions';
import { canCreateKeywords as canCreateKeywordsSelector } from '../../store/selectors/keywords';
import Logger from '../../services/Logger';
import l18n from '../../shared/strings';

import DetailsBlock from '../DetailsBlock';
import KeywordsDropdown from '../keywordsDropdown';

const Keywords = forwardRef(({ panelsVisibility, toggleVisibility }, ref) => {
  const dispatch = useDispatch();
  const disabled = useSelector(isUploadStarted);
  const error = useSelector((state) => state.import.errors.keywords);
  const numberOfSelectedFiles = useSelector((state) => state.import.selected.length);
  const requiredFields = useSelector(requiredCustomFieldsSelector);
  const editPermission = useSelector((state) => Boolean(state.import.permissions.editAssetKeywords));
  const canCreateKeywords = useSelector(canCreateKeywordsSelector);
  const keywords = useSelector((state) => state.import.fields.keywords);

  const handleBlur = useCallback(() => {
    Logger.log('User', 'UploadPanelAttachKeywords', {
      keywords_number: keywords.length,
      assets_number: numberOfSelectedFiles,
    });
  }, [keywords, numberOfSelectedFiles]);

  const handleSelect = useCallback((items) => {
    const value = Array.isArray(items) ? items : [items];
    dispatch(addToListField({ title: 'keywords', value }));
  }, [dispatch]);

  const handleDeselect = useCallback(({ _id }) => {
    dispatch(removeFromListField({ title: 'keywords', _id }));
  }, [dispatch]);

  const indicator = useMemo(() => getIndicator('keywords', requiredFields), [requiredFields]);

  return (
    <DetailsBlock
      dataQa="keywords"
      ref={ref}
      detailsPanelVisibility={panelsVisibility}
      toggleVisibility={toggleVisibility}
      blockName="importKeywordsVisibility"
      blockTitle={l18n.DETAILS.textKeywords}
      indicator={indicator}
      error={error}
      errorHighlight
      openByDefault={error || indicator !== null}
    >
      <div className="importFieldItem">
        <KeywordsDropdown
          placeholder={l18n.DROPDOWN.placeholderKeywords}
          placeholderIcon="emptyKeywords"
          icon="keyword"
          filterText={l18n.DROPDOWN.chooseKeywords}
          createText={l18n.DROPDOWN.createKeyword}
          createPlaceholderText={l18n.DROPDOWN.placeholderKeywordsCreate}
          checkedItems={keywords}
          onCheckedHandler={handleSelect}
          onUncheckedHandler={handleDeselect}
          canCreate={canCreateKeywords}
          onBlur={handleBlur}
          isOnlyCreate
          readOnly={!editPermission || disabled}
          disabled={disabled}
          error={error}
        />
      </div>
    </DetailsBlock>
  );
});

Keywords.propTypes = {
  panelsVisibility: panelsVisibilityShape.isRequired,
  toggleVisibility: func.isRequired,
};

export default memo(Keywords);
